/**
 * The scale a market band and a role's own range are drawn on together.
 *
 * **One wrong figure must not flatten the band.** The scale used to run from
 * the lowest figure to the highest, whichever side it came from. A role whose
 * pay was stored a thousand times out then took the top of it: a real screen
 * drew a market band of $276,000 to $348,000 beside a role range of
 * $195,600,000, and the band came out as two hundredths of one percent of the
 * bar. A single tick against the left edge, no fill visible, nothing a reader
 * could act on.
 *
 * So the role's range may stretch the scale to twice the top of the market and
 * no further. A role genuinely paying above the market still runs off to the
 * right, which is what that reading means; a figure that could not be a salary
 * pins at the end instead of erasing the band it was meant to be compared to.
 */

/** How far above the top of the market the scale will follow a role's range. */
export const SCALE_HEADROOM = 2

export interface Scale {
  low: number
  high: number
  /** Where a figure sits, as a percentage, never off either end of the bar. */
  at: (value: number) => number
}

/**
 * `market` is the percentiles the survey reported, `own` the role's own range.
 * Figures of zero or less are not drawn, because zero is how "not stated" is
 * held and a role paying nothing is a different claim to a role saying nothing.
 */
export function scaleFor(market: number[], own: number[] = []): Scale {
  const real = (v: number) => Number.isFinite(v) && v > 0
  const marketFigures = market.filter(real)
  const figures = [...marketFigures, ...own.filter(real)]
  if (figures.length === 0) return { low: 0, high: 0, at: () => 0 }

  const low = Math.min(...figures)
  const ceiling = marketFigures.length
    ? Math.max(...marketFigures) * SCALE_HEADROOM
    : Math.max(...figures)
  const high = Math.min(Math.max(...figures), Math.max(ceiling, low))
  const span = high - low

  return {
    low,
    high,
    at: (value: number) =>
      (span <= 0 ? 0 : Math.max(0, Math.min(100, ((value - low) / span) * 100))),
  }
}
